// Right-side antenna PTT button + interim transcript bubble + text input fallback.

export interface RadioUIEvents {
  onPress: () => void;        // user begins speaking
  onRelease: () => void;      // user stops speaking
  onTextSubmit?: (text: string) => void;  // text fallback
  onResume?: () => void;       // first interaction, opportunity to resume AudioContext
}

export class RadioUI {
  root: HTMLDivElement;
  private ptt: HTMLDivElement;
  private status: HTMLDivElement;
  private textBox: HTMLDivElement;
  private textInput: HTMLInputElement;
  private interim: HTMLDivElement;
  private events: RadioUIEvents;
  private pressed = false;
  textOnly: boolean;
  private warningShown = false;

  constructor(events: RadioUIEvents, opts: { textOnly: boolean }) {
    this.events = events;
    this.textOnly = opts.textOnly;

    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed', top: '54px', right: '12px', zIndex: '60',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px',
      pointerEvents: 'auto', userSelect: 'none',
    } as CSSStyleDeclaration);

    // PTT button.
    this.ptt = document.createElement('div');
    Object.assign(this.ptt.style, {
      width: '64px', height: '64px', borderRadius: '50%',
      background: 'rgba(255,200,100,0.25)', border: '2px solid rgba(255,220,150,0.7)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: '11px', cursor: 'pointer',
      touchAction: 'none', textAlign: 'center', lineHeight: '1.1',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    } as CSSStyleDeclaration);
    this.ptt.innerHTML = '📻<br/>电台';
    this.ptt.title = '长按说话 (Hold to talk)';
    this.root.appendChild(this.ptt);

    // Status line under the button.
    this.status = document.createElement('div');
    Object.assign(this.status.style, {
      background: 'rgba(8,12,28,0.7)', color: '#dde',
      padding: '4px 8px', borderRadius: '4px',
      font: '11px sans-serif', maxWidth: '160px', textAlign: 'right',
    } as CSSStyleDeclaration);
    this.status.textContent = opts.textOnly ? '语音不可用 · 输入文字' : '长按说话';
    this.root.appendChild(this.status);

    // Interim transcript bubble (above PTT, only visible while speaking).
    this.interim = document.createElement('div');
    Object.assign(this.interim.style, {
      position: 'fixed', bottom: '120px', left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(255,200,100,0.85)', color: '#222',
      padding: '8px 14px', borderRadius: '20px', font: '14px sans-serif',
      pointerEvents: 'none', zIndex: '70', opacity: '0', transition: 'opacity .2s',
      maxWidth: '70vw', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
    } as CSSStyleDeclaration);
    this.interim.textContent = '';
    document.body.appendChild(this.interim);

    // Text input row (always present; auto-shown when textOnly).
    this.textBox = document.createElement('div');
    Object.assign(this.textBox.style, {
      display: opts.textOnly ? 'flex' : 'none', gap: '4px',
      background: 'rgba(8,12,28,0.7)', padding: '4px', borderRadius: '6px',
    } as CSSStyleDeclaration);
    this.textInput = document.createElement('input');
    Object.assign(this.textInput.style, {
      width: '160px', background: 'rgba(255,255,255,0.06)', color: '#fff',
      border: '1px solid rgba(180,200,255,0.3)', borderRadius: '4px',
      padding: '4px 8px', font: '12px sans-serif',
    } as CSSStyleDeclaration);
    this.textInput.placeholder = '电台留言...';
    this.textInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.textInput.value.trim()) {
        this.events.onTextSubmit?.(this.textInput.value.trim());
        this.textInput.value = '';
      }
    });
    const sendBtn = document.createElement('button');
    sendBtn.textContent = '发送';
    Object.assign(sendBtn.style, {
      background: 'rgba(40,60,120,0.7)', color: '#fff',
      border: '1px solid rgba(180,200,255,0.3)', borderRadius: '4px',
      padding: '4px 10px', font: '11px sans-serif', cursor: 'pointer',
    } as CSSStyleDeclaration);
    sendBtn.addEventListener('click', () => {
      if (this.textInput.value.trim()) {
        this.events.onTextSubmit?.(this.textInput.value.trim());
        this.textInput.value = '';
      }
    });
    this.textBox.appendChild(this.textInput);
    this.textBox.appendChild(sendBtn);
    this.root.appendChild(this.textBox);

    // PTT events: pointer + touch + keyboard 'T'.
    const press = (ev?: Event) => {
      ev?.preventDefault();
      if (this.pressed) return;
      this.pressed = true;
      this.events.onResume?.();
      this.events.onPress();
      this.ptt.style.background = 'rgba(255,80,80,0.55)';
      this.status.textContent = '正在录音...';
    };
    const release = (ev?: Event) => {
      ev?.preventDefault();
      if (!this.pressed) return;
      this.pressed = false;
      this.events.onRelease();
      this.ptt.style.background = 'rgba(255,200,100,0.25)';
      this.status.textContent = opts.textOnly ? '语音不可用 · 输入文字' : '长按说话';
    };
    this.ptt.addEventListener('mousedown', press);
    this.ptt.addEventListener('mouseup', release);
    this.ptt.addEventListener('mouseleave', release);
    this.ptt.addEventListener('touchstart', press, { passive: false });
    this.ptt.addEventListener('touchend', release, { passive: false });
    this.ptt.addEventListener('touchcancel', release, { passive: false });
    window.addEventListener('keydown', (e) => { if (e.code === 'KeyT' && !e.repeat) press(); });
    window.addEventListener('keyup', (e) => { if (e.code === 'KeyT') release(); });

    document.body.appendChild(this.root);
  }

  showInterim(text: string): void {
    this.interim.textContent = text;
    this.interim.style.opacity = text ? '1' : '0';
  }

  hideInterim(): void {
    this.interim.style.opacity = '0';
  }

  showCantListenWarning(reason: string): void {
    if (this.warningShown) return;
    this.warningShown = true;
    this.status.textContent = `语音不可用 (${reason}) · 文字输入`;
    this.textBox.style.display = 'flex';
  }

  destroy(): void {
    this.root.remove();
    this.interim.remove();
  }
}
